/**
 * ArticlePresenceProvider — one shared presence/election connection for the
 * article editor page, consumed by BOTH the navy EditorNavbar (Take-Over UI) and
 * the editor body (read-only gating). Hosting it once here avoids two websocket
 * connections / a split-brain election.
 *
 * It also bridges the "reload HEAD before control" rule: the editor registers a
 * reload handler, and `requestTakeOver()` runs it (fetch latest content/version,
 * and for an agent holder force-release its lock) BEFORE claiming the lock, so a
 * taking-over user never edits a stale snapshot.
 */
import React, { createContext, useCallback, useContext, useRef } from "react";
import {
  useArticlePresence,
  UseArticlePresenceReturn,
} from "src/hooks/useArticlePresence";

export interface ArticlePresenceContextValue extends UseArticlePresenceReturn {
  /** Reload HEAD (+ agent force-release) then claim the lock. */
  requestTakeOver: () => Promise<void>;
  /** The editor registers its "reload HEAD before control" handler here. */
  registerTakeOverReload: (fn: (() => Promise<void>) | null) => void;
}

const INERT: ArticlePresenceContextValue = {
  active: false,
  others: [],
  me: { displayName: "", color: "#9CA3AF" },
  setDisplayName: () => {},
  isHolder: false,
  holder: null,
  takeOver: () => {},
  canEdit: true,
  settled: true, // inert (presence off) → never read-only, never "connecting"
  requestTakeOver: async () => {},
  registerTakeOverReload: () => {},
};

const ArticlePresenceContext = createContext<ArticlePresenceContextValue>(INERT);

export const useArticlePresenceContext = (): ArticlePresenceContextValue =>
  useContext(ArticlePresenceContext);

export const ArticlePresenceProvider: React.FC<{
  articleId: string;
  enabled?: boolean;
  children: React.ReactNode;
}> = ({ articleId, enabled = true, children }) => {
  const presence = useArticlePresence(articleId, { enabled });
  const reloadRef = useRef<(() => Promise<void>) | null>(null);

  const registerTakeOverReload = useCallback(
    (fn: (() => Promise<void>) | null) => {
      reloadRef.current = fn;
    },
    []
  );

  const takeOver = presence.takeOver;
  const requestTakeOver = useCallback(async () => {
    // Reload HEAD content + bump the backend version (force-releasing any agent
    // lock) BEFORE granting edit control. Fail CLOSED on BOTH fronts:
    //  1. No reload handler registered (editor not mounted / not ready) → we have no
    //     way to enforce the take-over on the backend, so we must NOT grant control.
    //  2. The handler throws (backend take-over rejected) → we do NOT call takeOver(),
    //     so a failed take-over never hands the editor to a session that didn't win
    //     the lock on the backend. The reload handler surfaces the error snack.
    if (!reloadRef.current) {
      console.warn(
        "[presence] take-over requested before the editor registered its reload " +
          "handler — not granting control (no backend enforcement available)."
      );
      return;
    }
    await reloadRef.current(); // throws on backend failure → control NOT granted below
    takeOver();
  }, [takeOver]);

  const value: ArticlePresenceContextValue = {
    ...presence,
    requestTakeOver,
    registerTakeOverReload,
  };

  return (
    <ArticlePresenceContext.Provider value={value}>
      {children}
    </ArticlePresenceContext.Provider>
  );
};
